import { call, put, takeLatest, takeEvery, select, debounce, all, delay } from 'redux-saga/effects';

import {
  importSource as importSourceApi,
  deleteSource as deleteSourceApi,
  updateSource as updateSourceApi,
  createSource as createSourceApi,
  getSources as getSourcesApi,
  getSourceTypes as getSourceTypesApi,
  getSourceHealth as getSourceHealthApi,
  getVariables as getVariablesApi,
  getVariable as getVariableApi,
  createVariables as createVariablesApi,
  updateVariable as updateVariableApi,
  deleteVariable as deleteVariableApi,
  uploadAOExcel as uploadRiskConfigExcelAPI,
  checkConnectivity as checkConnectivityApi,
  updateTfrClassificationConfiguration as updateTfrClassificationConfigurationApi,
  updateSwitchgearCounterConfiguration as updateSwitchgearCounterConfigurationApi,
} from '../../services';
import { INTERNAL_DATA_SOURCE, TYPE_LABEL_OVERRIDES, TYPE_ORDER } from '../../constants';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import { getSite } from '../sites';
import {
  REFRESH_VALUES,
  setPollingActive,
  setPollingActiveDone,
  afterSiteCleared,
} from '../application';
import {
  REQUEST_SOURCES,
  DELETE_SOURCE,
  UPDATE_SOURCE,
  CREATE_SOURCE,
  IMPORT_SOURCE,
  REQUEST_CONNECTIVITY,
  UPDATE_SOURCE_VARIABLE,
  DELETE_SOURCE_VARIABLE,
  REQUEST_SITE_VARIABLES,
  CREATE_SOURCE_VARIABLES,
  UPLOAD_RISK_CONFIG_EXCEL,
  UPLOAD_TFR_CLASSIFICIATION_CONFIG,
  UPLOAD_SWITCHGEAR_MONITORING_CONFIG,
  receiveSources,
  receiveConnectivity,
  receiveSyncStatus,
  receiveVariable,
  receiveSiteVariables,
  removeVariable,
  importSource,
} from './actions';
import { getSources, getSyncedSources } from './selectors';

export function* doRequestSources({ siteId, withVariables }) {
  try {
    const { values: sources } = yield call(getSourcesApi, siteId);
    const { values: sourceTypes } = yield call(getSourceTypesApi);
    if (withVariables) {
      const variablesArray = yield all(
        sources.map((source) => call(getVariablesApi, source.id, { extended: true }))
      );
      const siteVariables = variablesArray.flatMap((v) => v.values);
      const variableIndex = {};
      for (const v of siteVariables) {
        variableIndex[v.id] = v;
      }
      yield put(receiveSources(sources, sourceTypes, variableIndex, siteId));
    } else {
      yield put(receiveSources(sources, sourceTypes, null, siteId));
    }
  } catch (error) {
    console.error('Unable to fetch sources:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getSources', 'error')()));
    yield put(receiveSources([], [], {}, siteId));
  }
}

export function* doDeleteSource(action) {
  const { sourceId, siteId } = action;

  try {
    yield deleteSourceApi(sourceId);
    yield doRequestSources({ siteId });
    yield put(displayNotification(getNotification('deleteSource', 'success')(sourceId)));
  } catch (error) {
    console.error('Unable to delete source:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteSource', 'error')(sourceId)));
  }
}

export function* doCreateSource(action) {
  const { data: source, onSuccess, onError } = action;
  const { options: { import: importVariables = {} } = {} } = source;
  let created;
  try {
    created = yield createSourceApi(source);
    yield put(displayNotification(getNotification('createSource', 'success')()));
    if (onSuccess) yield call(onSuccess);
  } catch (error) {
    console.error('Unable to create source:', error);
    yield call(checkOnline);
    const apiMessage = error?.response?.data?.message;
    const errorKey =
      error?.response?.data?.error === 'ConnectionFailed'
        ? 'createSourceConnectionFailed'
        : 'createSource';
    yield put(displayNotification(getNotification(errorKey, 'error')(apiMessage || error.message)));
    if (onError) yield call(onError, apiMessage || error.message);
  }
  if (
    created &&
    ((importVariables.endpoint && importVariables.enabled) || created.type === 'dec')
  ) {
    yield put(importSource(source.site, created.id, () => null));
  } else if (created) {
    yield doRequestSources({ siteId: source.site });
  }
}

export function* doUpdateSource(action) {
  const { siteId, sourceId, data, successMessage, errorMessage, onSuccess, onError } = action;

  try {
    yield updateSourceApi(sourceId, data);
    yield doRequestSources({ siteId });
    yield put(
      displayNotification(getNotification('updateSource', 'success')(sourceId, successMessage))
    );
    if (onSuccess) yield call(onSuccess);
  } catch (error) {
    console.error('Unable to update source:', error);
    yield call(checkOnline);
    const apiMessage = error?.response?.data?.message;
    const errorKey =
      error?.response?.data?.error === 'ConnectionFailed'
        ? 'updateSourceConnectionFailed'
        : 'updateSource';
    yield put(
      displayNotification(getNotification(errorKey, 'error')(sourceId, apiMessage || errorMessage))
    );
    if (onError) yield call(onError, apiMessage || error.message);
  }
}

export function* doGetSourceSyncHealth(action) {
  yield put(setPollingActive('sources'));
  try {
    const site = yield select((state) => getSite(state, action.siteId));
    const sources = yield select(getSources);
    const syncedSources = yield select(getSyncedSources);

    if (site && sources.some((s) => syncedSources.includes(s.type))) {
      const syncStatus = yield call(getSourceHealthApi, site.id, site.org);
      if (syncStatus) {
        yield put(receiveSyncStatus(action.siteId, syncStatus));
      }
    }
  } catch (error) {
    console.error('Unable to fetch source health:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getSourceSyncHealth', 'error')()));
  }
  yield put(setPollingActiveDone('sources'));
}

export function* doRequestVariable(action) {
  const { sourceId, variableId } = action;
  try {
    const variable = yield call(getVariableApi, sourceId, variableId);
    yield put(receiveVariable(variable));
  } catch (error) {
    console.error('Unable to fetch signal: ', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getVariables', 'error')()));
  }
}

export function* doRequestVariables() {
  try {
    const sources = yield select(getSources);
    const variablesArray = yield all(
      sources.map((source) => call(getVariablesApi, source.id, { extended: true }))
    );
    const variables = variablesArray.flatMap((v) => v.values);
    const variableIndex = {};
    for (const v of variables) {
      variableIndex[v.id] = v;
    }
    yield put(receiveSiteVariables(variableIndex));
  } catch (error) {
    console.error('Unable to fetch signals: ', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getVariables', 'error')()));
  }
}

export function* doImportSourceVariables(action) {
  const {
    siteId,
    sourceId,
    body,
    onComplete = () => {
      // do something on onComplete
    },
  } = action;

  try {
    const { added, updated, removed } = yield importSourceApi(sourceId, body);
    yield call(doRequestVariables);
    const message = `Imported source signals: added (${added}), updated (${updated}), removed (${removed})`;
    yield put(displayNotification(getNotification('importSourceVars', 'success')(message)));
  } catch (error) {
    console.error('Unable to import source variables:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('importSourceVars', 'error')(error.message)));
  }
  yield doRequestSources({ siteId });
  yield call(onComplete);
}

export function* doCreateSourceVariables(action) {
  const { sourceId, data, successCallback, failureCallback, doNotify } = action;
  const coercedData = Array.isArray(data) ? data : [data];
  const isAttributeType = !!coercedData[0].asset_id;
  const title = isAttributeType ? 'Attribute' : 'Signal';
  const singleCreate = coercedData.length === 1;

  try {
    const { values, errors } = yield createVariablesApi(sourceId, coercedData);

    if (isAttributeType && errors.length > 0) {
      throw new Error(JSON.stringify(errors));
    }
    if (!isAttributeType && singleCreate) {
      yield put(receiveVariable(values[0]));
    } else if (!isAttributeType) {
      yield call(doRequestVariables);
    }

    if (doNotify) {
      yield put(
        displayNotification(
          getNotification('createVariable', 'success')(
            singleCreate ? coercedData[0].name : '',
            title
          )
        )
      );
    }
    if (successCallback) {
      yield call(successCallback);
    }
  } catch (error) {
    console.error('Unable to create signal:', error);
    const handleUniqueNameError = error?.message?.includes('already exists') && failureCallback;
    if (handleUniqueNameError) {
      yield call(failureCallback, ['Attribute name must be unique to component.']);
    }
    if (doNotify && !handleUniqueNameError) {
      yield call(checkOnline);
      yield put(
        displayNotification(
          getNotification('createVariable', 'error')(singleCreate ? coercedData[0].name : '', title)
        )
      );
    }
  }
}

export function* doUpdateSourceVariable(action) {
  const { sourceId, variableId, data, successCallback, failureCallback, doNotify } = action;
  const title = data.asset_id ? 'Attribute' : 'Signal';
  try {
    const updatedVariable = yield call(updateVariableApi, sourceId, variableId, data);
    yield call(doRequestVariable, { sourceId, variableId: updatedVariable.id });
    if (doNotify) {
      yield put(
        displayNotification(
          getNotification('updateVariable', 'success')(data.name, updatedVariable.id, title)
        )
      );
    }
    if (successCallback) {
      yield call(successCallback);
    }
  } catch (error) {
    console.error('Unable to update variable:', error);
    const handleUniqueNameError =
      error?.response?.data?.message?.includes('already exists') && failureCallback;
    if (handleUniqueNameError) {
      yield call(failureCallback, ['Attribute name must be unique to component.']);
    }
    if (doNotify && !handleUniqueNameError) {
      yield call(checkOnline);
      yield put(
        displayNotification(
          getNotification('updateVariable', 'error')(data.name, variableId, title)
        )
      );
    }
  }
}

export function* doDeleteSourceVariable(action) {
  const { sourceId, variableId, callback, doNotify, isAttribute } = action;
  const title = isAttribute ? 'Attribute' : 'Signal';

  try {
    yield deleteVariableApi(sourceId, variableId);
    yield put(removeVariable(variableId));
    if (doNotify) {
      yield put(
        displayNotification(getNotification('deleteVariable', 'success')(variableId, title))
      );
    }
    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    console.error('Unable to delete signal:', error);
    if (doNotify) {
      yield call(checkOnline);
      yield put(displayNotification(getNotification('deleteVariable', 'error')(variableId, title)));
    }
  }
}

export function* doUploadRiskConfigExcel(action) {
  const { sourceId, siteId, file, org, onComplete } = action;
  try {
    const fd = new FormData();
    fd.append('site', siteId);
    fd.append('file', file);
    fd.append('org', org);
    const response = yield call(uploadRiskConfigExcelAPI, sourceId, fd);
    yield response.status === 1
      ? put(displayNotification(getNotification('uploadRiskConfigExcel', 'success')()))
      : put(displayNotification(getNotification('uploadRiskConfigExcel', 'warning')()));
  } catch (error) {
    console.error('Error in importing the file.', error);
    yield put(displayNotification(getNotification('uploadRiskConfigExcel', 'error')()));
  }
  yield call(onComplete);
}

export function* doUploadTFRClassificationConfiguration(action) {
  const { siteId, sourceId, configuration, fileName } = action;
  try {
    yield call(updateTfrClassificationConfigurationApi, sourceId, configuration, fileName);
    yield doRequestSources({ siteId });
    yield put(displayNotification(getNotification('uploadTFRClassificationConfig', 'success')()));
  } catch (error) {
    console.error('Error in uploading the file.', error);
    yield put(displayNotification(getNotification('uploadTFRClassificationConfig', 'error')()));
  }
}

const versionlessType = (type) => (type ? type.split('@')[0] : '');

const parseTypeLabel = (type) => {
  if (!type) return 'Unknown';
  const base = versionlessType(type);
  if (TYPE_LABEL_OVERRIDES[base]) return TYPE_LABEL_OVERRIDES[base];
  const last = base.split('.').pop() || base;
  return last.replaceAll(/([a-z])([A-Z])/g, '$1 $2');
};

const typeOrderIndex = (type) => {
  const index = TYPE_ORDER.indexOf(versionlessType(type));
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const aggregateStatus = (devices) => {
  if (devices.some((d) => d.status === 0)) return 0;
  if (devices.some((d) => d.status === 2)) return 2;
  return 1;
};

/*
 * Builds a hierarchical representation of a source and its devices, grouped by type.
 * An aggregated `status` is determinied at the group level and the source level
 * using a worst-case aggregation strategy, while the source's `rawStatus` is
 * preserved in order to reflect the source's actual connectivity status for
 * external requests for timeseries data.
 */
const buildAggregatedSourceConnectivity = ({ sourceId, name, devices, status: providedStatus }) => {
  const groupsByType = new Map();
  for (const device of devices) {
    if (!groupsByType.has(device.type)) {
      groupsByType.set(device.type, {
        type: device.type,
        label: parseTypeLabel(device.type),
        devices: [],
      });
    }
    groupsByType.get(device.type).devices.push(device);
  }

  const groups = [...groupsByType.values()]
    .map((group) => ({
      ...group,
      status: aggregateStatus(group.devices),
    }))
    .toSorted((a, b) => typeOrderIndex(a.type) - typeOrderIndex(b.type));

  return {
    sourceId,
    name,
    sourceName: name,
    groups,
    status: aggregateStatus(groups),
    rawStatus: providedStatus ?? 2, // retained to use when requesting timeseries data
  };
};

function* doUploadSwitchgearMonitoringConfiguration(action) {
  const { siteId, sourceId, configuration, fileName } = action;
  try {
    yield call(updateSwitchgearCounterConfigurationApi, sourceId, configuration, fileName);
    yield doRequestSources({ siteId });
    yield put(
      displayNotification(getNotification('uploadSwitchgearMonitoringConfig', 'success')())
    );
  } catch (error) {
    console.error('Error in uploading the file.', error);
    yield put(displayNotification(getNotification('uploadSwitchgearMonitoringConfig', 'error')()));
  }
}

export function* doRequestConnectivity() {
  let filteredSources;
  try {
    const sources = yield select(getSources);
    // Only request connectivity for sources that are expected to have it
    // (sources with connectivity configured)
    filteredSources = sources.filter((source) => {
      const { type: sourceType, options: { connectivity = [], checkConnectivity = false } = {} } =
        source;
      if (sourceType === INTERNAL_DATA_SOURCE) return false;
      if (sourceType === 'custom' && !checkConnectivity) return false;
      if (!connectivity || !Array.isArray(connectivity) || connectivity.length === 0) return false;
      return true;
    });
    const connectivity = yield all(filteredSources.map((s) => checkConnectivityApi(s.id)));

    /*
     * Each source returns its own connectivity entry along with device entries.
     * Initially, the source-level entry indicates the connectivity status of the source itself,
     * while the device entries reflect the connectivity status of individual devices.
     *
     * This will map over each source, group the devices, and build a connectivity
     * hierarchy which reflects a bubble-up of connectivity status from devices to the source.
     * In other words, this will derive an ancestor's status from its descendants (worst-status-wins)
     * so the source's status reflects the groups' statuses and a group's status reflects
     * the status of its devices. (offline (0) > unknown (2) > online (1)).
     */
    const sourceConnectivity = filteredSources
      .map((source, index) => {
        const sourceEntries = connectivity[index];
        const sourceStatus = sourceEntries.find((entry) => entry.isSource)?.value ?? 2;
        const sourceDescendants = sourceEntries
          .filter((entry) => !entry.isSource)
          .map((entry) => ({
            ...entry,
            // Prefer the explicit `deviceName` for display; fall back to `name`.
            name: entry.deviceName || entry.name,
            status: entry.value ?? 2,
          }));

        return buildAggregatedSourceConnectivity({
          sourceId: source.id,
          name: source.name || 'Source',
          devices: sourceDescendants,
          status: sourceStatus,
        });
      })
      .filter((source) => source.groups.length > 0);

    yield put(receiveConnectivity(sourceConnectivity));
  } catch (error) {
    console.error('Unable to get connectivity status:', error);
    yield call(checkOnline);
    // if we should have connectivity info but an error occurred, return sources with an unknown status (2)
    if (filteredSources?.length > 0) {
      const unknownConnectivity = filteredSources.map((source) => ({
        sourceId: source.id,
        name: source.name || 'Source',
        devices: [],
        status: 2,
        groups: [],
      }));
      yield put(receiveConnectivity(unknownConnectivity));
    }
    yield put(displayNotification(getNotification('connectivity', 'error')()));
  }
}

export function* doRefreshSources() {
  yield call(doRequestConnectivity);
}

export function* pollForInternalSource(siteId) {
  while (true) {
    const { values: sources } = yield call(getSourcesApi, siteId);
    if (sources.some((source) => source.type === INTERNAL_DATA_SOURCE)) {
      break;
    }
    yield delay(500);
  }
}

export const sagas = [
  takeLatest(REQUEST_SOURCES, afterSiteCleared(doRequestSources)),
  takeEvery(DELETE_SOURCE, doDeleteSource),
  takeLatest(UPDATE_SOURCE, doUpdateSource),
  takeLatest(CREATE_SOURCE, doCreateSource),
  takeLatest(IMPORT_SOURCE, doImportSourceVariables),
  takeEvery(REQUEST_SITE_VARIABLES, doRequestVariables),
  takeLatest(CREATE_SOURCE_VARIABLES, doCreateSourceVariables),
  takeLatest(UPDATE_SOURCE_VARIABLE, doUpdateSourceVariable),
  takeLatest(DELETE_SOURCE_VARIABLE, doDeleteSourceVariable),
  takeLatest(UPLOAD_RISK_CONFIG_EXCEL, doUploadRiskConfigExcel),
  takeLatest(UPLOAD_TFR_CLASSIFICIATION_CONFIG, doUploadTFRClassificationConfiguration),
  takeLatest(UPLOAD_SWITCHGEAR_MONITORING_CONFIG, doUploadSwitchgearMonitoringConfiguration),
  takeLatest(REQUEST_CONNECTIVITY, afterSiteCleared(doRequestConnectivity)),
  debounce(500, REFRESH_VALUES, afterSiteCleared(doRefreshSources)),
];
